import './ProfileActivityCard.css';
import { Card, CardContent, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAnalytics } from 'context/AnalyticsContext';
import { useProfile } from 'context/ProfileContext';
import { useTranslation } from 'react-i18next';
import { LineChart } from '@mui/x-charts/LineChart';
import { useMemo, useRef, useState, useEffect, useId } from 'react';
import { buildCurvePath } from 'utils';
import { CHART_SKELETON_COLOR as SKELETON_COLOR, CHART_SKELETON_POINTS as SKELETON_POINTS } from 'utils/constants';

const ActivityEmptyState = ({ message }: { message: string }) => (
    <div className="activity-empty">
        <Typography variant="body2" className="activity-empty-text">
            {message}
        </Typography>
    </div>
);

const ChartSkeleton = () => {
    const clipId = useId();
    const path = buildCurvePath(SKELETON_POINTS);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(540);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const W = 540;
    const H = 210;
    const xAxisY = 178;
    const scaleX = containerWidth / W;

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative', height: H }}>
            <style>{`
                @keyframes chart-sk-pulse {
                    0%   { opacity: 1; }
                    50%  { opacity: 0.4; }
                    100% { opacity: 1; }
                }
                .chart-sk { animation: chart-sk-pulse 2s ease-in-out 0.5s infinite; }
            `}</style>

            {/* Layer 1: line + axis — stretched horizontally */}
            <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}>

                <defs>
                    {/* evenodd clipPath: full rect minus ellipses at each point = line never draws inside circles */}
                    <clipPath id={clipId}>
                        <path fillRule="evenodd" d={[
                            `M 0 0 H ${W} V ${H} H 0 Z`,
                            ...SKELETON_POINTS.map(p => {
                                const rx = 7 / scaleX;
                                const ry = 7;
                                return `M ${p.x + rx} ${p.y} A ${rx} ${ry} 0 1 0 ${p.x - rx} ${p.y} A ${rx} ${ry} 0 1 0 ${p.x + rx} ${p.y}`;
                            })
                        ].join(' ')} />
                    </clipPath>
                </defs>

                <line className="chart-sk" x1={0} y1={xAxisY} x2={W} y2={xAxisY}
                    stroke={SKELETON_COLOR} strokeWidth="1.5" />

                <path className="chart-sk" d={path} fill="none"
                    stroke={SKELETON_COLOR} strokeWidth="2" strokeLinecap="round"
                    clipPath={`url(#${clipId})`} />

                {SKELETON_POINTS.map((p, i) => (
                    <rect key={i} className="chart-sk"
                        x={p.x - 14} y={xAxisY + 6} width="28" height="7" rx="3"
                        fill={SKELETON_COLOR} />
                ))}
            </svg>

            {/* Layer 2: points — real pixel coords, no distortion */}
            <svg width="100%" height={H} viewBox={`0 0 ${containerWidth} ${H}`}
                style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}>
                {SKELETON_POINTS.map((p, i) => (
                    <circle key={i} className="chart-sk"
                        cx={p.x * scaleX} cy={p.y} r={5}
                        fill="transparent" stroke={SKELETON_COLOR} strokeWidth="2" />
                ))}
            </svg>
        </div>
    );
};

const ProfileActivityCard = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { activity: { data: activityData, loading } } = useAnalytics();
    const { state: profileState } = useProfile();
    const { dailyData, totalClicks, totalVisits } = activityData ?? { dailyData: [], totalClicks: 0, totalVisits: 0 };

    const isEmpty = !loading && totalVisits === 0;
    const emptyMessage = profileState.isPublished
        ? t('dashboard.main.activity.empty.noVisits')
        : t('dashboard.main.activity.empty.notPublished');

    const { pData, xLabels } = useMemo(() => ({
        pData: dailyData.map((d: any) => d.visits),
        xLabels: dailyData.map((d: any) => d.label),
    }), [dailyData]);

    const bestDay = useMemo(() => {
        if (!Array.isArray(dailyData) || !dailyData.length) return null;
        const best = (dailyData as any[]).reduce((best: any, d: any) => {
            const v = Number(d?.visits);
            return isFinite(v) && v > (Number(best?.visits) || 0) ? d : best;
        }, dailyData[0]);
        return best?.label && Number(best.visits) > 0 ? best : null;
    }, [dailyData]);

    // Theil-Sen estimator: robust trend via median of all pairwise slopes.
    // Unlike OLS, a single spike day can't distort the result.
    const trend = useMemo(() => {
        try {
            const data = dailyData as any[];
            if (!Array.isArray(data) || data.length < 7) return null;

            // Sanitize visits: must be a finite number, default to 0
            const visits = data.map((d: any) => {
                const v = Number(d?.visits);
                return isFinite(v) && v >= 0 ? v : 0;
            });

            const totalVisits = visits.reduce((s, v) => s + v, 0);
            // Not enough signal to show a meaningful trend
            if (totalVisits < 5) return null;

            const activeDays = visits.filter(v => v > 0).length;
            // Single spike — no pattern to calculate
            if (activeDays < 2) return null;

            // Theil-Sen: compute slope for every pair of days
            const slopes: number[] = [];
            for (let i = 0; i < visits.length; i++) {
                for (let j = i + 1; j < visits.length; j++) {
                    slopes.push((visits[j] - visits[i]) / (j - i));
                }
            }

            // Median slope — naturally ignores outlier spikes
            slopes.sort((a, b) => a - b);
            const mid = Math.floor(slopes.length / 2);
            const slope = slopes.length % 2 === 0
                ? (slopes[mid - 1] + slopes[mid]) / 2
                : slopes[mid];

            // ±0.2 visits/day threshold filters noise from low-traffic profiles
            if (slope > 0.2) return 'up';
            if (slope < -0.2) return 'down';
            return 'flat';
        } catch {
            return null;
        }
    }, [dailyData]);

    const trendColor = trend === 'up' ? 'success.main' : trend === 'down' ? 'error.dark' : 'text.secondary';
    const trendLabel = trend === null ? '—'
        : trend === 'up' ? t('dashboard.main.activity.trendUp')
        : trend === 'down' ? t('dashboard.main.activity.trendDown')
        : t('dashboard.main.activity.trendFlat');

    return (
        <Card variant="outlined" className="dashboard-card activity">
            <CardContent className="activity-content">
                <div className="activity-header">
                    <Typography variant="body1" component="p" className="activity-title">
                        {t('dashboard.main.activity.title')}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={90} height={16} />
                    ) : (
                        <Typography variant="body2" className="activity-period">
                            {t('dashboard.main.activity.period')}
                        </Typography>
                    )}
                </div>

                <div className="activity-chart">
                    {loading ? (
                        <ChartSkeleton />
                    ) : isEmpty ? (
                        <ActivityEmptyState message={emptyMessage} />
                    ) : (
                        <LineChart
                            key={i18n.language}
                            xAxis={[{ scaleType: 'point', data: xLabels, height: 28, hideTooltip: true }]}
                            yAxis={[isMobile ? { width: 28 } : { position: 'none' }]}
                            series={[{ data: pData, showMark: true, color: '#e8e7e7', label: t('dashboard.main.activity.visitsLabel') }]}
                            height={180}
                            margin={{ top: 24, right: 28, bottom: 0, left: isMobile ? 0 : 24 }}
                        />
                    )}
                </div>

                <div className="activity-footer">
                    <div className="activity-stat">
                        <span className="activity-stat-label">{t('dashboard.main.activity.clicks')}</span>
                        {loading
                            ? <Skeleton variant="text" width={36} height={20} />
                            : <span className="activity-stat-value">{totalClicks.toLocaleString()}</span>}
                    </div>
                    <div className="activity-stat">
                        <span className="activity-stat-label">{t('dashboard.main.activity.bestDay')}</span>
                        {loading
                            ? <Skeleton variant="text" width={52} height={20} />
                            : <span className="activity-stat-value">{bestDay?.label ?? '—'}</span>}
                    </div>
                    <div className="activity-stat">
                        <span className="activity-stat-label">{t('dashboard.main.activity.trend')}</span>
                        {loading
                            ? <Skeleton variant="text" width={44} height={20} />
                            : <span className="activity-stat-value" style={{ color: trendColor }}>
                                {trendLabel}
                            </span>}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProfileActivityCard;
